import { Alert } from 'antd';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import useListDomainsForNav from '../../useListDomainsForNav';
import DomainNode from './DomainNode';
import { Domain } from '../../../../types.generated';
import { ComponentSpinner } from '../../../shared/ComponentSpinner';
import { useTranslation } from '../../../../i18n/useTranslation';
import { DOMAIN_NAVIGATOR_SCROLL_THRESHOLD } from '../../utils';

const NavigatorWrapper = styled.div`
    overflow: auto;
    overflow-anchor: none;
    font-size: 14px;
    padding: 8px 8px 16px 16px;
    background-color: #ffffff;
    max-height: calc(100% - 53px);
`;

interface Props {
    showIcon?: boolean;
    domainUrnToHide?: string;
    isDomainAccessible?: boolean;
    selectDomainOverride?: (domain: Domain) => void;
    onLoadingChange?: (loading: boolean) => void;
}

export default function DomainNavigator({
    domainUrnToHide,
    selectDomainOverride,
    isDomainAccessible,
    showIcon,
    onLoadingChange,
}: Props) {
    const { t } = useTranslation();
    const { domains, error, loading, hasMore, loadMore, loadingMore } = useListDomainsForNav({});
    const scrollWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        onLoadingChange?.(loading);
    }, [loading, onLoadingChange]);

    const handleScroll = useCallback(() => {
        const el = scrollWrapperRef.current;
        if (!el || !hasMore) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < DOMAIN_NAVIGATOR_SCROLL_THRESHOLD) {
            loadMore();
        }
    }, [hasMore, loadMore]);

    return (
        <NavigatorWrapper ref={scrollWrapperRef} onScroll={handleScroll} className="f-domain-navigator">
            {error && <Alert message={t('FAILED_TO_LOAD_DOMAINS_UNEXPECTED_ERROR')} showIcon type="error" />}
            {domains?.map((domain) => (
                <DomainNode
                    key={domain.urn}
                    domain={domain as Domain}
                    numDomainChildren={domain.children?.total || 0}
                    domainUrnToHide={domainUrnToHide}
                    selectDomainOverride={selectDomainOverride}
                    showIcon={showIcon}
                    isDomainAccessible={isDomainAccessible}
                />
            ))}
            {loadingMore && <ComponentSpinner />}
        </NavigatorWrapper>
    );
}
